import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import AutoResponderSection from "./components/AutoResponderSection";
import WorkflowsList from "./components/WorkflowsList";
import WorkflowAnalyticsSection from "./components/WorkflowAnalyticsSection";
import { autoResponderSources, type AutoResponderSource } from "@/mocks/workflowsData";
import { fetchApi } from "@/services/apiService";

export default function WorkflowsPage() {
  const navigate = useNavigate();
  const [masterEnabled, setMasterEnabled] = useState(false);
  const [sources, setSources] = useState<AutoResponderSource[]>(autoResponderSources);
  const [workflows, setWorkflows] = useState<unknown[]>([]);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  useEffect(() => {
    fetchApi<unknown[]>('/workflows', undefined, [])
      .then((data) => {
        if (data) setWorkflows(data);
      })
      .catch((err) => console.error("Error fetching workflows:", err));
  }, []);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleToggleWorkflow = (id: string) => {
    fetchApi(`/workflows/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: true }) }).catch(() => {});
    showToast(`Workflow ${id === "wf-1" ? "status" : "status"} updated`);
  };

  const handleDeleteWorkflow = (id: string) => {
    fetchApi(`/workflows/${id}`, { method: 'DELETE' }).catch(() => {});
    showToast("Workflow deleted successfully");
  };

  const handleDuplicateWorkflow = (id: string) => {
    showToast("Workflow duplicated");
  };

  return (
    <CrmLayout title="Workflow Automation">
      <div className="p-5 md:p-6 space-y-5">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Workflow Automation</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Automate lead nurturing and qualification with WhatsApp bot and auto-responders
            </p>
          </div>
          <button
            onClick={() => navigate("/settings/workflows/new")}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            Create Workflow
          </button>
        </div>

        {/* Section 1: Auto-Responder */}
        <AutoResponderSection
          masterEnabled={masterEnabled}
          onMasterToggle={setMasterEnabled}
          sources={sources}
          onSourcesChange={setSources}
        />

        {/* Section 2: Active Workflows */}
        <WorkflowsList
          onToggleWorkflow={handleToggleWorkflow}
          onDeleteWorkflow={handleDeleteWorkflow}
          onDuplicateWorkflow={handleDuplicateWorkflow}
        />

        {/* Section 3: Workflow Analytics */}
        <WorkflowAnalyticsSection />
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 right-5 z-[100] px-4 py-3 rounded-lg shadow-sm text-sm font-medium animate-slide-in bg-green-500 text-white">
          <div className="flex items-center gap-2.5">
            <i className="ri-check-line text-base" />
            {toast.message}
          </div>
        </div>
      )}
    </CrmLayout>
  );
}